/**
 * Workforce agents — shared contract constants (BAL-8063, Phase 0).
 *
 * Single source of truth for the string-literal domains behind the Phase-0 agent-substrate
 * tables (`workforce_agent_runs`, `workforce_agent_run_events`, `workforce_agent_proposals`
 * — `component_versions` and `workforce_agents` were both dropped by the manifest-cut +
 * derivation reworks). The Drizzle `pgEnum`s
 * (unified-api `schema.ts`) and the FE/BE schemas all reference THESE arrays, so the DB enum,
 * the API contract, and the UI can never drift. Same pattern as
 * `workforce-task-automation.ts` (BAL-7917).
 *
 * See `docs/plans/WORKFORCE_AGENT_INTEGRATION_PLAN.md` Appendix B + §9.
 */

// Component-kind domain behind the `workforce_component_kind` pgEnum. The version-table
// subsystem was cut (PREFLIGHT §4 minimal cut — runs stamp a jsonb manifest instead); the
// enum type is retained so the P5 return of the version registry is additive.
export const WORKFORCE_COMPONENT_KINDS = ['prompt', 'model', 'tool', 'policy'] as const;
export type WorkforceComponentKind = (typeof WORKFORCE_COMPONENT_KINDS)[number];

// NOTE (BAL-8063 derivation, 2026-07-24): the registry domains (owner/status) were removed
// with the `workforce_agents` table — a task definition carrying `agent_config` IS the agent
// assignment (see `schemas/workforce-agents.ts` for the `WorkforceAgentConfig` contract; its
// `execution` domain is WORKFORCE_AGENT_EXECUTIONS from workforce-task-automation.ts). The
// BYO-agent registry returns at P6.

// Agent archetype — the broad capability class an `agent_config` may declare (optional).
// Survives the registry drop: it labels the CONFIG, not a registry row.
export const WORKFORCE_AGENT_ARCHETYPES = ['judgment', 'collection', 'comms'] as const;
export type WorkforceAgentArchetype = (typeof WORKFORCE_AGENT_ARCHETYPES)[number];

/**
 * The canonical agent tool vocabulary — the ONLY names `agent_config.tools` may contain
 * (review-fleet #25/30).
 *
 * `tools` is a NARROWING allowlist: the judgment loop offers `bundle ∩ tools`. That makes a typo
 * silently destructive rather than loud — `'screenshoot'` intersects to nothing, so the agent
 * runs with a smaller toolset (or none) and simply behaves worse, with no error anywhere. Typing
 * the domain here turns that into a write-time validation failure.
 *
 * Lives in `common` because it is a two-sided contract: unified-api validates `agent_config` on
 * write against it, and the workforce-agents runtime composes its offered bundle from the same
 * names. `PHASE1_TOOL_BUNDLE` (the runtime's versioned descriptors) is pinned against this list
 * by a parity test — a tool added to the runtime without being added here fails that test rather
 * than becoming unconfigurable.
 */
export const WORKFORCE_AGENT_TOOL_NAMES = [
  'read_task',
  'read_report',
  'browser',
  // BAL-8180: act on the current page by ref — the interaction primitive. Landed on the runtime
  // side (PHASE1_TOOL_BUNDLE) with the capability; the vocabulary is what types
  // `agent_config.tools`, so omitting it would make every config offering the tool fail
  // validation.
  'browser_act',
  'screenshot',
  'inspect_image',
  'http_fetch',
  // BAL-8177 shipped these two on the runtime (`judgmentTools` composes both) but added them
  // neither here nor to PHASE1_TOOL_BUNDLE. The parity test compared only those two lists, so
  // both agreed by both being wrong. While missing: `toolBundleHash` described a bundle the run
  // did not offer, and no `agent_config.tools` could narrow to them — the shipped judgment
  // definition names both and would have failed validation the moment anything validated it.
  'http_request',
  'tls_inspect',
  'serp',
  // BAL-8213: person screening (sanctions / PEP / adverse-media). Composes the same vendor lane
  // as `serp`, but is its own tool because the screening skill's `agent_config.tools` narrows to
  // it specifically — omitting it here would make that allowlist fail validation on write.
  'screen_person',
  // BAL-8212: structured field extraction + compare-to-declared. Landed on the runtime side
  // (PHASE1_TOOL_BUNDLE) with the feature; the vocabulary is what types `agent_config.tools`,
  // so omitting it would make every config that offers the tool fail validation.
  'extract_fields',
  'store_document',
  'list_evidence',
  // BAL-8217: FCA Financial Services Register lookup. A dedicated tool rather than a prompted
  // `http_fetch` because the register API authenticates on request HEADERS, and the page lane
  // takes a URL and nothing else.
  'fca_register',
  // BAL-8217: delivers a declared skill's body on demand. The catalog (key + description) rides
  // the prompt tail; this fetches the procedure, so a task declaring several does not carry all
  // of them on every step.
  'load_skill',
] as const;
export type WorkforceAgentToolName = (typeof WORKFORCE_AGENT_TOOL_NAMES)[number];

/**
 * The skill vocabulary (BAL-8217) — the keys `agent_config.skills` may name.
 *
 * A SKILL is a reusable PROCEDURE ("how to check a UK regulator"), distinct from the two things
 * either side of it: `default_prompt` carries what THIS task is, and a tool carries the transport.
 * Procedures are shared across task types, so a skill exists to stop the same paragraph being
 * copy-pasted into every definition that needs the check.
 *
 * Same two-sided-contract reasoning as WORKFORCE_AGENT_TOOL_NAMES above: the vocabulary is shared
 * so unified-api and the workforce-agents runtime cannot disagree about what a key means.
 *
 * IMPORTANT — there is NO write-time validation today. `WorkforceAgentConfigSchema`'s only runtime
 * use in unified-api is the tasks-read RESPONSE schema; no create/update route validates it, and
 * seeds write the jsonb directly. The closed union buys TYPE-level safety for code that constructs
 * a config, not a runtime guard. The actual enforcement is at RUN START (`resolveSkills` /
 * `assertSkillToolsAllowed`), which fails the run loudly rather than degrading it. Only the KEYS live here — the bodies live in
 * `services/workforce-agents/src/skills/` and are pinned to this list by a parity test, because
 * agent prose in `common` would ride into every frontend bundle that imports the package.
 *
 * Skills are code, not rows (author-ratified 2026-08-06, after first designing a `workforce_skills`
 * table): that keeps this vocabulary CLOSED, so a mistyped key is a compile-time error for any code
 * that constructs a config — and a LOUD run-start failure for anything that bypasses the types
 * (seeds writing jsonb). Migrating to a table later is contained; `agent_config.skills` does not
 * change shape, though it would forfeit the closed union.
 */
export const WORKFORCE_AGENT_SKILL_KEYS = [
  // BAL-8217 — UK FCA Financial Services Register presence check. First instance of the
  // regulatory-presence shape (BAL-8226); DE Impressum and JP CTA follow with no API connector.
  'uk-fca-register',
] as const;
export type WorkforceAgentSkillKey = (typeof WORKFORCE_AGENT_SKILL_KEYS)[number];

// Run lifecycle. Live = { running } — the dispatch-dedupe partial unique index on
// `workforce_agent_runs(case_id)` keys off exactly this value (plan §C.1.1).
// `queued` RETIRED (2026-07-30, @amiron; dropped from the pgEnum + live-index predicate at
// migration consolidation): no code path creates a queued run — runs are born `running` at
// claim. Temporal owns queueing/backpressure; D-15 dedupes statelessly at trigger time. A
// queue-first dispatch mode, if ever wanted, is reintroduced deliberately at D-15 with that
// analysis as the bar to clear.
export const WORKFORCE_AGENT_RUN_STATUSES = [
  'running',
  'succeeded',
  'failed',
  'gave_up',
  'cancelled',
] as const;
export type WorkforceAgentRunStatus = (typeof WORKFORCE_AGENT_RUN_STATUSES)[number];

// A run in one of these still holds the one-live-run-per-case slot.
export const WORKFORCE_AGENT_RUN_LIVE_STATUSES = [
  'running',
] as const satisfies readonly WorkforceAgentRunStatus[];

// Proposal lifecycle (§9.1). Extendable to the E4/E5 executor lifecycle via `ALTER TYPE … ADD VALUE`.
export const WORKFORCE_PROPOSAL_STATUSES = ['proposed', 'approved', 'rejected'] as const;
export type WorkforceProposalStatus = (typeof WORKFORCE_PROPOSAL_STATUSES)[number];

/**
 * WHY a run ended where it did — the ORTHOGONAL axis to the CLEAR/FLAG verdict (BAL-8212).
 *
 * The verdict says what the agent concluded; the outcome cause says whether that conclusion
 * is worth anything. Four of six deliberately-hard runs ended FLAG and NOT ONE was a risk
 * finding — the real causes were a budget cap, a citation deadlock, an HTTP 404 and a step
 * cap. All four landed in the review queue looking identical to merchant risk, which poisons
 * both the queue and any FLAG-rate metric.
 *
 * ALWAYS set by the HARNESS from deterministic run facts (`classifyOutcomeCause` in
 * workforce-agents) — NEVER by the model: the model's self-report of why it stopped is
 * exactly the thing that cannot be trusted here.
 *
 * This array is the single source for all three enforcement points: the agent-lane route
 * schema, the admin queue's filter, and the `workforce_agent_runs_outcome_cause_check` DB
 * constraint (migration 0251, widened by 0255). Adding a value means updating the constraint too.
 *
 *  - `risk_finding`      — the investigation actually completed; the verdict is a real finding.
 *  - `budget_exhausted`  — the run hit its spend cap (the §9.7 partial-verdict degradation).
 *  - `step_cap_reached`  — the run hit `agent_config.limits.maxSteps`.
 *  - `source_blocked`    — we were REFUSED. A bot-management challenge or an outright refusal
 *                          (403/429/503). The host is up and chose not to serve us.
 *  - `source_offline`    — the host NEVER ANSWERED. DNS failure, connection refused, connect
 *                          timeout. Nothing to escalate to; the merchant may simply be gone.
 *  - `source_unreachable`— **DEPRECATED / LEGACY — never emit this on a new run.** The pre-split
 *                          bucket that collapsed bot-management blocks, DNS failures, parked
 *                          domains, dead servers AND plain wrong URLs into one value. Retained
 *                          because existing rows carry it and the original failure detail was
 *                          never recorded, so they cannot honestly be reclassified. Split into
 *                          `source_blocked` / `source_offline` by BAL-8212 (ADR-0013 §3: the
 *                          provider ladder escalates on `blocked` and must NOT escalate on
 *                          `offline`, so one merged value left the ladder with no signal).
 *  - `evidence_uncitable`— the G14 citation gate exhausted, or forced the verdict to abandon
 *                          its citations entirely.
 *  - `tool_error`        — a tool/vendor/platform I/O failure ended the run.
 *  - `internal_error`    — anything else terminal (the honest remainder — never a silent bucket).
 *
 * A host that ANSWERS is not a source failure. A 404/410/any 4xx WITHOUT bot-management markers
 * means the server responded and the page simply is not there — that falls through to the normal
 * path (`risk_finding`), it is NOT `source_blocked` and NOT `source_offline`. Getting this wrong
 * is how we described `thomann.de` as "bot-protected" when the URL merely 404'd and the site
 * served a bare `curl` an HTTP 200 (`docs/research/browser-stealth-and-cdp-2026-08.md` §0).
 */
export const WORKFORCE_OUTCOME_CAUSES = [
  'risk_finding',
  'budget_exhausted',
  'step_cap_reached',
  'source_blocked',
  'source_offline',
  // Legacy — kept only so historical rows stay inside the vocabulary. See the doc block above.
  'source_unreachable',
  'evidence_uncitable',
  'tool_error',
  'internal_error',
] as const;
export type WorkforceOutcomeCause = (typeof WORKFORCE_OUTCOME_CAUSES)[number];

/**
 * The causes a new run must NEVER emit — historical rows only. The DB CHECK stays wider than
 * {@link WORKFORCE_EMITTABLE_OUTCOME_CAUSES} precisely so those rows remain legal.
 *
 * (The doc here used to describe the exact COMPLEMENT of the value — "everything except
 * source_unreachable" — so an author trusting it would have gated emission to only the
 * deprecated cause.)
 */
export const WORKFORCE_OUTCOME_CAUSES_LEGACY = ['source_unreachable'] as const;
export type WorkforceLegacyOutcomeCause = (typeof WORKFORCE_OUTCOME_CAUSES_LEGACY)[number];

/**
 * The causes a NEW run is allowed to emit — the vocabulary minus the legacy set. DERIVED, so
 * adding a cause to {@link WORKFORCE_OUTCOME_CAUSES} or retiring one into the legacy set cannot
 * leave the two disagreeing. `classifyOutcomeCause` is pinned against this.
 */
export const WORKFORCE_EMITTABLE_OUTCOME_CAUSES = WORKFORCE_OUTCOME_CAUSES.filter(
  (cause): cause is Exclude<WorkforceOutcomeCause, WorkforceLegacyOutcomeCause> =>
    !(WORKFORCE_OUTCOME_CAUSES_LEGACY as readonly string[]).includes(cause),
);
export type WorkforceEmittableOutcomeCause = (typeof WORKFORCE_EMITTABLE_OUTCOME_CAUSES)[number];

/**
 * The causes that mean the run REACHED A CONCLUSION about the merchant, as opposed to stopping
 * for a reason of its own (a cap, a deadlock, a blocked source).
 *
 * A set, not a literal, because the same predicate was hard-coded in THREE deployables — the
 * dashboard's `labels.ts`, unified-api's `trace-attribution.ts`, and the agent's classifier —
 * so adding a second concluding cause would have silently desynced them: the queue painting a
 * concluded run as "could not complete" while attribution stopped travelling, every test green.
 */
export const WORKFORCE_CONCLUDED_OUTCOME_CAUSES: readonly WorkforceOutcomeCause[] = [
  'risk_finding',
];

/** True when the cause means the run concluded about the merchant. NULL/unknown ⇒ false. */
export const isConcludedOutcomeCause = (cause: string | null | undefined): boolean =>
  cause != null &&
  (WORKFORCE_CONCLUDED_OUTCOME_CAUSES as readonly string[]).includes(cause.toLowerCase());

// Structured verdict values (§9.1, the PoC `Verdict` shape) — per subtask and for the rollup.
// Not a pgEnum (the verdict is jsonb); the API contract and future FE render from HERE.
export const WORKFORCE_VERDICT_VALUES = ['CLEAR', 'FLAG'] as const;
export type WorkforceVerdictValue = (typeof WORKFORCE_VERDICT_VALUES)[number];

// Metering write kinds (P0-8, §9.7) — what a `POST /workforce/metering` dollar amount was
// spent on. Budget covers vendor $ not just tokens (G5: proxy/captcha loops are cheap on
// tokens, expensive on vendors). No pgEnum behind this — it keys the API contract and the
// `vendor_executions` attribution; shared here so the agent shell (P1-11) can't drift.
// 'registry' (BAL-8217): official-registry lookups (FCA today; DE/JP registries next). Added while
// the only connector is FREE and meters $0.00 — deliberately, not as dead config. A kind added
// later, when the first PAID registry arrives, would leave every call before it missing from
// attribution entirely, and would need a vocabulary change mid-flight. The row still earns its
// keep at $0.00: it is the per-tool call count in the same ledger as every other vendor.
export const WORKFORCE_METERING_KINDS = [
  'llm',
  'proxy',
  'captcha',
  'browser',
  'serp',
  'registry',
] as const;
export type WorkforceMeteringKind = (typeof WORKFORCE_METERING_KINDS)[number];
/**
 * The run's flight record (BAL-8063, PREFLIGHT §4 minimal cut ratified 2026-07-24) — the
 * RUNTIME facts observed at claim/serve time, stamped as `workforce_agent_runs.manifest`.
 * Content-addressed hashes give full G6/G10 attribution without a version-table subsystem
 * (the registry tables return only with a replay-driven need — P5).
 *
 * Task-side authored intent (prompt, agent_config, budget) lives frozen on the TASK;
 * the manifest records what actually ran — config proposes, manifest disposes (G3: the
 * served model may differ from any requested one via pre-first-token fallback).
 */
export type WorkforceRunManifest = {
  /** sha256 of the runtime's harness/system prompt (the task's instruction prompt is on the task row). */
  harnessPromptHash: string;
  /** The SERVED model id (post-fallback), e.g. 'azure/gpt-4o'. */
  model: string;
  /** sha256 of the tool-bundle the runtime shipped with. */
  toolBundleHash: string;
  /** sha256 (or id) of the policy/guardrail set in force for this customer. */
  policyHash: string;
  /**
   * sha256 of the SKILL BODIES this run was given (BAL-8217) — the procedures loaded into the
   * prompt tail from `agent_config.skills`.
   *
   * OPTIONAL, and absent when the run declared no skills: it is deliberately NOT in
   * {@link WORKFORCE_RUN_MANIFEST_REQUIRED_FIELDS}, because the P0-2 runs-table guard enforces
   * that list and a new required field would invalidate every run row already stamped.
   *
   * Hashes the bodies rather than the keys, mirroring `harnessPromptHash` (which hashes the
   * prompt TEXT): a key list cannot distinguish a run before and after a skill was rewritten.
   *
   * Scope note: since bodies are delivered on demand by `load_skill`, this covers what the run was
   * OFFERED, not necessarily what it read. Which skills a run actually loaded is recoverable from
   * the event log's `load_skill` tool calls — the manifest's job is to pin the versions on offer.
   */
  skillsHash?: string;
};

export const WORKFORCE_RUN_MANIFEST_REQUIRED_FIELDS = [
  'harnessPromptHash',
  'model',
  'toolBundleHash',
  'policyHash',
] as const satisfies ReadonlyArray<keyof WorkforceRunManifest>;

/**
 * M2M token wire contract (BAL-8063 P0-4) — the three strings the auth-gateway SIGNER and the
 * unified-api VERIFIER must agree on byte-for-byte. They previously lived as hand-duplicated
 * literals in both services; a silent divergence in any of them rejects every token with a
 * generic 401 that is undiagnosable in an incident. Shared here (plain strings — no crypto, so
 * this stays safe for the browser bundles that import `@ballerine/common`) rather than as a
 * shared crypto package: signing and verifying are asymmetric halves with no shareable logic,
 * only this shared vocabulary.
 *
 * `M2M_TOKEN_ISSUER` is deliberately consumer-agnostic — it is the issuer of Ballerine's whole
 * machine-to-machine backbone, not of the workforce lane. New consumers get a new AUDIENCE
 * under the same issuer (see ADR-0012).
 */
export const M2M_TOKEN_ISSUER = 'auth-gateway';
export const M2M_TOKEN_ALGORITHM = 'RS256';

/** Audience of the workforce agent lane — the first M2M audience. */
export const WORKFORCE_JWT_AUDIENCE = 'workforce-api';

/**
 * The DISPATCHER token's `scope` claim (BAL-8063) — the second string the gateway SIGNER and
 * the unified-api VERIFIER must agree on byte-for-byte, and for the same reason as the
 * audience above: it is a pure equality check on both sides, so a rename on one side alone
 * produces tokens that verify cryptographically and are then refused for wrong scope. The
 * failure mode is total (every dispatch call 401s, the worker stops dispatching) and silent
 * in CI, because each service's suite would pass against its own copy of the literal.
 *
 * Lives here rather than beside either guard for that reason: neither service owns it.
 */
export const WORKFORCE_DISPATCH_SCOPE = 'workforce:dispatch';

/**
 * The workforce JWT's domain claim names, snake_case per the ratified design.
 *
 * The LAST unguarded drift vector between the two services: the gateway builds this
 * object literally and unified-api reads the properties back, so a rename on one side
 * alone produces tokens that verify cryptographically and then fail claim mapping with
 * a generic 401. Because nothing calls the mint yet, that would not surface until a
 * deployed round trip. Both suites assert their own shape against this list, so the
 * drift fails a test instead.
 *
 * Order is not significant; membership is.
 */
export const WORKFORCE_JWT_CLAIM_NAMES = [
  'agent_id',
  'run_id',
  'customer_id',
  'lease_id',
  'action_classes',
] as const;
export type WorkforceJwtClaimName = (typeof WORKFORCE_JWT_CLAIM_NAMES)[number];

// Worklog entry visibility (P0-7, ratified 2026-07-22): the agent's narrated trail lives in a
// dedicated `workforce_worklog_entries` table, PRIVATE BY DEFAULT — the server hardcodes
// 'internal' today; 'external_reviewer'/'customer' exist for a later product decision, never client-set.
export const WORKFORCE_WORKLOG_VISIBILITIES = [
  'internal',
  'external_reviewer',
  'customer',
] as const;
export type WorkforceWorklogVisibility = (typeof WORKFORCE_WORKLOG_VISIBILITIES)[number];
